import React, { Component, forwardRef } from 'react';
import PropTypes from 'prop-types';
import { withRouterLegacy } from 'core/routerUtils';
import { withTranslation } from 'react-i18next';
import isEmpty from 'lodash/isEmpty';
import isObject from 'lodash/isObject';
import { AlertNotice, SimpleModal, Stepper } from 'shared/components';
import { TCRenewalSubmitted } from 'features/tc-renewal/';
import { INSTITUTION_STATUS } from 'shared/constants';

import './TCRenewalSteps.scss';

const basePath = '/test-center-';

class TCRenewalSteps extends Component {
  constructor(props) {
    super(props);
    if (props.forwardedRef) {
      props.forwardedRef.current = this;
    }
    this.state = {
      step: this.getStep(props),
      serverError: '',
      redirectLink: '',
      showExitModal: false,
      showSubmitModal: false,
      isBackModal: false,
    };
    this.wrapperRef = React.createRef(null);
    this.handleClickOutside = this.handleClickOutside.bind(this);
    this.handleContinue = this.handleContinue.bind(this);
    this.handleRedirect = this.handleRedirect.bind(this);
    this.getUrl = this.getUrl.bind(this);
    this.getStep = this.getStep.bind(this);
    this.confirmExitModal = this.confirmExitModal.bind(this);
    this.cancelExitModal = this.cancelExitModal.bind(this);
    this.confirmSubmitModal = this.confirmSubmitModal.bind(this);
    this.cancelSubmitModal = this.cancelSubmitModal.bind(this);
    this.redirect = this.redirect.bind(this);
    this.handleBackSteps = this.handleBackSteps.bind(this);
    this.disableContinueBtn = this.disableContinueBtn.bind(this);

    const self = this;
    window.onbeforeunload = () => {
      if (self.child?.checkIsDirty?.() && isEmpty(self.state.redirectLink)) {
        return this.props.t('testCenter.exitModal.message');
      }
    };
  }

  getUrl = (stepIdx, id) => {
    const { steps, TCUrlAction } = this.props;
    return `${basePath}${TCUrlAction}/${steps[stepIdx].name}${id ? `/${id}` : ''}`;
  };

  getStep(props) {
    const stepName = props.match?.params?.step;
    if (stepName === 'successfully-submitted') {
      return -1; // Special case for success page
    }
    if (stepName) {
      return props.steps.findIndex((s) => s.name === stepName);
    }
    return -1;
  }
  componentDidMount() {
    document.addEventListener('mousedown', this.handleClickOutside, false);
  }

  componentDidUpdate(prevProps) {
    const currentStep = this.getStep(this.props);
    const prevStep = this.getStep(prevProps);

    if (currentStep !== prevStep) {
      this.setState({
        step: currentStep,
        serverError: '',
      });
    }
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside, false);
  }

  handleClickOutside = (event) => {
    if (this.wrapperRef.current && !this.wrapperRef.current.contains(event.target)) {
      if (
        event.target.closest('.tooltip-menu-item') ||
        event.target.parentNode.nodeName === 'A' ||
        event.target.nodeName === 'A'
      ) {
        event.stopPropagation();
        event.preventDefault();
        this.setState({
          showExitModal: true,
          isBackModal: false,
          redirectLink:
            event.target.pathname ||
            event.target.parentNode.pathname ||
            event.target.closest('.tooltip-menu-item')?.pathname ||
            '',
        });
      }
    }
  };

  async handleContinue() {
    const continueInfo = await this.child.handleContinue();
    const nextStep = isObject(continueInfo) ? false : continueInfo;

    if (nextStep) {
      const id = this.props.match.params.tcCycleId;

      if (this.state.step < this.props.steps.length - 1) {
        this.props.history.push(this.getUrl(this.state.step + 1, id));
      } else {
        this.setState({
          showSubmitModal: true,
        });
      }
    } else if (continueInfo.error) {
      this.setState({
        serverError: 'errors.saveError',
      });
    } else if (this.state.serverError) {
      this.setState({
        serverError: '',
      });
    }
  }

  handleRedirect(e, redirectLink) {
    if (this.child && this.child.checkIsDirty && this.child.checkIsDirty()) {
      this.setState({
        showExitModal: true,
        redirectLink,
        isBackModal: !redirectLink,
      });
      // show modal for exit button
    } else if (redirectLink) {
      this.setState({
        showExitModal: true,
        redirectLink,
        isBackModal: false,
      });
    } else {
      this.setState(
        {
          redirectLink,
        },
        this.redirect
      );
    }
  }

  redirect() {
    const { redirectLink, step } = this.state;

    this.disableContinueBtn(false);

    if (redirectLink) {
      if (redirectLink.includes('test-center')) {
        const stepsBack = step + 1;
        this.props.history.go(-stepsBack);
      } else {
        this.props.history.replace(redirectLink);
      }
    } else {
      this.props.history.goBack();
    }
  }

  handleBackSteps(currentStepId, toStepName) {
    const goBackStep = this.props.steps.find((st) => st.name === toStepName);
    const stepsBack = currentStepId - goBackStep.id;
    this.disableContinueBtn(false);

    this.props.history.go(-stepsBack);
  }

  cancelExitModal() {
    this.setState((prevState) => ({
      showExitModal: !prevState.showExitModal,
      redirectLink: '',
    }));
  }

  cancelSubmitModal() {
    this.setState((prevState) => ({
      showSubmitModal: !prevState.showSubmitModal,
    }));
  }

  confirmExitModal() {
    this.setState({
      showExitModal: false,
    });
    this.redirect();
  }

  async confirmSubmitModal() {
    const { actions, currentUser, testCenterCycle, TCUrlAction } = this.props;
    const id = testCenterCycle.id;

    this.setState({
      showSubmitModal: false,
    });

    await actions.updateTcStatus(id, {
      status:
        TCUrlAction === 'renewal'
          ? INSTITUTION_STATUS.renewalComplete
          : INSTITUTION_STATUS.establishComplete,
    });

    actions.getUserTestCenters(currentUser.id);

    this.props.history.push(`${basePath}${TCUrlAction}/successfully-submitted/${id}`);
  }

  disableContinueBtn(disable) {
    this.setState({
      isContinueBtnDisabled: disable,
    });
  }

  render() {
    const { match, testCenterCycle, steps, TCUrlAction } = this.props;
    const { step, serverError, isBackModal, isContinueBtnDisabled } = this.state;
    const { step: stepName } = match.params;

    // Render success page
    if (stepName === 'successfully-submitted') {
      return (
        <TCRenewalSubmitted
          history={this.props.history}
          stepsBack={steps.length + 1}
          TCUrlAction={TCUrlAction}
        />
      );
    }

    // Find and render the current step component
    const currentStep = steps[step];
    const StepComponent = currentStep?.component;

    return (
      !isEmpty(testCenterCycle) && (
        <div ref={this.wrapperRef}>
          {step > 0 && step < steps.length ? (
            <div className="renewal-head-section">
              <Stepper current={step - 1} stepsTotal={steps.slice(1)} />
            </div>
          ) : null}
          {StepComponent && (
            <StepComponent
              ref={(instance) => {
                this.child = instance;
              }}
              handleBackSteps={this.handleBackSteps}
              disableContinueBtn={this.disableContinueBtn}
              currentStepId={currentStep.id}
              TCUrlAction={TCUrlAction}
            />
          )}
          {serverError && (
            <div className="mt-3">
              <AlertNotice type="error" message={this.props.t(serverError)} />
            </div>
          )}
          {step != -1 && (
            <div
              className={
                step < steps.length - 1 ? 'buttons-container' : 'buttons-container last-step'
              }>
              <button className="btn btn-warn" onClick={this.handleRedirect}>
                {this.props.t('common.labels.back')}
              </button>
              <button
                className="btn btn-action"
                onClick={this.handleContinue}
                disabled={isContinueBtnDisabled ? 'disabled' : ''}>
                {step < steps.length - 1
                  ? this.props.t('common.labels.continue')
                  : this.props.t(
                      TCUrlAction === 'renewal'
                        ? 'testCenter.submitRenewalLabel'
                        : 'common.labels.submit'
                    )}
              </button>
            </div>
          )}
          <SimpleModal
            isOpen={this.state.showExitModal}
            onCancel={this.cancelExitModal}
            onConfirm={this.confirmExitModal}
            message={
              isBackModal
                ? this.props.t('testCenter.backModal.message')
                : this.props.t(
                    TCUrlAction === 'renewal'
                      ? 'testCenter.exitModal.message'
                      : 'testCenter.exitModal.messageEstablishment'
                  )
            }
            confirmLabel={
              isBackModal
                ? this.props.t('common.labels.yes')
                : this.props.t('testCenter.exitModal.yesLabel')
            }
            cancelLabel={this.props.t('common.labels.no')}
            headline={
              isBackModal
                ? this.props.t('testCenter.backModal.title')
                : this.props.t(
                    TCUrlAction === 'renewal'
                      ? 'testCenter.exitModal.title'
                      : 'testCenter.exitModal.titleEstablishment'
                  )
            }
            buttonsReverseOrder={!!isBackModal}
          />

          <SimpleModal
            isOpen={this.state.showSubmitModal}
            onCancel={this.cancelSubmitModal}
            onConfirm={this.confirmSubmitModal}
            message={this.props.t(
              TCUrlAction === 'renewal'
                ? 'testCenter.submitModal.message'
                : 'testCenter.submitEstablishmentModal.message'
            )}
            confirmLabel={this.props.t('common.labels.yes')}
            cancelLabel={this.props.t('common.labels.no')}
            headline={this.props.t(
              TCUrlAction === 'renewal'
                ? 'testCenter.submitModal.title'
                : 'testCenter.submitEstablishmentModal.title'
            )}
          />
        </div>
      )
    );
  }
}

TCRenewalSteps.propTypes = {
  // Props from react-router (via withRouterLegacy)
  match: PropTypes.shape({
    params: PropTypes.shape({
      tcCycleId: PropTypes.string,
      step: PropTypes.string,
    }),
  }).isRequired,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
    replace: PropTypes.func.isRequired,
    go: PropTypes.func.isRequired,
    goBack: PropTypes.func.isRequired,
  }).isRequired,

  // Props from parent component
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      component: PropTypes.elementType,
    })
  ).isRequired,
  TCUrlAction: PropTypes.string.isRequired,
  testCenterCycle: PropTypes.shape({
    id: PropTypes.number,
  }).isRequired,
  currentUser: PropTypes.shape({
    id: PropTypes.number,
  }).isRequired,
  actions: PropTypes.shape({
    updateTcStatus: PropTypes.func.isRequired,
    getUserTestCenters: PropTypes.func.isRequired,
  }).isRequired,
  t: PropTypes.func.isRequired,
};

const TranslatedTCRenewalSteps = withTranslation()(TCRenewalSteps);
const TCRenewalStepsWithRef = forwardRef((props, ref) => (
  <TranslatedTCRenewalSteps {...props} forwardedRef={ref} />
));

export default withRouterLegacy(TCRenewalStepsWithRef);
